import React, { useState, useEffect, useCallback } from 'react';
import { 
  RefreshCw, Download, CheckCircle, AlertCircle, Clock, Package, 
  Plus, Loader2, ExternalLink, Link, Unlink, DollarSign, Eye, 
  Database, Store, ChevronDown, Settings, Key, Play
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VendorSyncHub = () => {
  const [activeTab, setActiveTab] = useState('sync');
  
  // Vendors state
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [loadingVendors, setLoadingVendors] = useState(true);
  
  // Sync state
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncHistory, setSyncHistory] = useState([]);
  
  // Items state
  const [vendorItems, setVendorItems] = useState([]);
  const [itemsTotal, setItemsTotal] = useState(0);
  const [itemsLinked, setItemsLinked] = useState(0);
  const [itemsPage, setItemsPage] = useState(1);
  const [itemsFilter, setItemsFilter] = useState('all');
  
  // Credentials state
  const [hasCredentials, setHasCredentials] = useState({});
  
  // Loading states
  const [loading, setLoading] = useState(false);
  const [creatingProducts, setCreatingProducts] = useState(false);
  const [updatingPrices, setUpdatingPrices] = useState(false);
  const [priceUpdateStatus, setPriceUpdateStatus] = useState(null);

  // Fetch all vendors with sync capability
  const fetchVendors = useCallback(async () => {
    try {
      setLoadingVendors(true);
      const res = await fetch(`${API_URL}/api/vendors`);
      if (res.ok) {
        const data = await res.json();
        // Filter to vendors with adapter_type = playwright_scraper
        const syncableVendors = (data.vendors || []).filter(v => 
          v.adapter_type === 'playwright_scraper' || v.slug === 'engraveca' || v.slug === '4over'
        );
        setVendors(syncableVendors);
        
        // Select first vendor by default
        if (syncableVendors.length > 0 && !selectedVendor) {
          setSelectedVendor(syncableVendors[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
    } finally {
      setLoadingVendors(false);
    }
  }, [selectedVendor]);

  // Check credentials for vendors
  const checkCredentials = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/vendor-credentials`);
      if (res.ok) {
        const creds = await res.json();
        const credMap = {};
        creds.forEach(c => {
          credMap[c.vendor_code] = c.is_active && c.password_set;
        });
        setHasCredentials(credMap);
      }
    } catch (error) {
      console.error('Error checking credentials:', error);
    }
  }, []);

  // Fetch vendor items
  const fetchVendorItems = useCallback(async () => {
    if (!selectedVendor) return;
    
    try {
      const linkedParam = itemsFilter === 'linked' ? '&linked=true' : 
                          itemsFilter === 'unlinked' ? '&linked=false' : '';
      const res = await fetch(`${API_URL}/api/vendors/${selectedVendor.id}/items?page=${itemsPage}&limit=20${linkedParam}`);
      if (res.ok) {
        const data = await res.json();
        setVendorItems(data.items || []);
        setItemsTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching vendor items:', error);
    }
  }, [selectedVendor, itemsPage, itemsFilter]);

  // Fetch sync status and history
  const fetchSyncStatus = useCallback(async () => {
    if (!selectedVendor) return;
    
    try {
      const [statusRes, historyRes] = await Promise.all([
        fetch(`${API_URL}/api/vendor-sync/${selectedVendor.id}/status`),
        fetch(`${API_URL}/api/vendor-sync/${selectedVendor.id}/history?limit=5`)
      ]);
      
      if (statusRes.ok) {
        const status = await statusRes.json();
        setSyncStatus(status);
      }
      
      if (historyRes.ok) {
        const history = await historyRes.json();
        setSyncHistory(history.history || []);
      }
    } catch (error) {
      console.error('Error fetching sync status:', error);
    }
  }, [selectedVendor]);

  // Initial load
  useEffect(() => {
    fetchVendors();
    checkCredentials();
  }, [fetchVendors, checkCredentials]);

  // Load data when vendor changes
  useEffect(() => {
    if (selectedVendor) {
      fetchSyncStatus();
      setItemsTotal(selectedVendor.total_items || 0);
      setItemsLinked(selectedVendor.linked_products || 0);
    }
  }, [selectedVendor, fetchSyncStatus]);

  // Load items when tab or filter changes
  useEffect(() => {
    if (activeTab === 'items' && selectedVendor) {
      fetchVendorItems();
    }
  }, [activeTab, fetchVendorItems, selectedVendor]);

  // Poll for sync status while running
  useEffect(() => {
    if (syncStatus?.status === 'running') {
      const interval = setInterval(fetchSyncStatus, 3000);
      return () => clearInterval(interval);
    }
  }, [syncStatus?.status, fetchSyncStatus]);

  // Start sync
  const handleStartSync = async (dryRun = false) => {
    if (!selectedVendor) return;
    
    setSyncing(true);
    try {
      const response = await fetch(
        `${API_URL}/api/vendor-sync/${selectedVendor.id}/sync?download_images=true&dry_run=${dryRun}`, 
        { method: 'POST' }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Sync failed');
      }
      
      toast.success(dryRun ? "Preview Started" : "Sync Started", {
        description: `Syncing products from ${selectedVendor.name}...`
      });
      
      fetchSyncStatus();
    } catch (error) {
      toast.error("Error", { description: error.message });
    } finally {
      setSyncing(false);
    }
  };

  // Create products from vendor items
  const handleCreateProducts = async () => {
    if (!selectedVendor) return;
    
    setCreatingProducts(true);
    try {
      const response = await fetch(
        `${API_URL}/api/vendor-sync/${selectedVendor.id}/create-products?create_all=true&markup_percent=150`, 
        { method: 'POST' }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create products');
      }
      
      const result = await response.json();
      toast.success("Products Created", {
        description: `Created ${result.created} products with 150% markup`
      });
      
      fetchVendors();
      fetchVendorItems();
    } catch (error) {
      toast.error("Error", { description: error.message });
    } finally {
      setCreatingProducts(false);
    }
  };

  // Update prices for 4over products
  const handleUpdatePrices = async () => {
    if (!selectedVendor || selectedVendor.slug !== '4over') return;
    
    setUpdatingPrices(true);
    try {
      const response = await fetch(
        `${API_URL}/api/vendor-sync/${selectedVendor.id}/update-prices?markup_percent=150`, 
        { method: 'POST' }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to start price update');
      }
      
      toast.success("Price Update Started", {
        description: "Fetching real-time prices from 4over. This may take a few minutes..."
      });
      
      // Start polling for price update status
      fetchPriceUpdateStatus();
    } catch (error) {
      toast.error("Error", { description: error.message });
    } finally {
      setUpdatingPrices(false);
    }
  };

  // Fetch price update status
  const fetchPriceUpdateStatus = useCallback(async () => {
    if (!selectedVendor) return;
    
    try {
      const res = await fetch(`${API_URL}/api/vendor-sync/${selectedVendor.id}/price-update-status`);
      if (res.ok) {
        const status = await res.json();
        setPriceUpdateStatus(status);
      }
    } catch (error) {
      console.error('Error fetching price update status:', error);
    }
  }, [selectedVendor]);

  // Poll for price update status while running
  useEffect(() => {
    if (priceUpdateStatus?.status === 'running') {
      const interval = setInterval(fetchPriceUpdateStatus, 5000);
      return () => clearInterval(interval);
    }
  }, [priceUpdateStatus?.status, fetchPriceUpdateStatus]);

  // Clear items
  const handleClearItems = async () => {
    if (!selectedVendor) return;
    if (!window.confirm('This will delete all vendor items and unlink products. Are you sure?')) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/vendor-sync/${selectedVendor.id}/items?also_delete_products=false`, 
        { method: 'DELETE' }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to clear items');
      }
      
      toast.success("Items Cleared", {
        description: "All vendor items have been deleted. Run sync to re-import."
      });
      
      fetchVendors();
      fetchVendorItems();
      fetchSyncStatus();
    } catch (error) {
      toast.error("Error", { description: error.message });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  const formatPrice = (price, priceType) => {
    if (!price) return '-';
    return priceType === 'per_sqft' ? `$${price}/sqft` : `$${price}`;
  };

  const isSyncing = syncStatus?.status === 'running';
  const needsCredentials = selectedVendor?.slug === '4over' && !hasCredentials['4over'];

  if (loadingVendors) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-gold-500" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="vendor-sync-hub">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-ivory-800">Vendor Product Sync</h1>
          <p className="text-ivory-500 font-body">Sync and manage products from external vendors</p>
        </div>
      </div>

      {/* Vendor Selector */}
      <Card className="bg-ivory-100 border border-ivory-300/50">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-gold-500/10 flex items-center justify-center">
                <Store className="w-6 h-6 text-gold-600" />
              </div>
              <div className="flex-1">
                {vendors.length > 1 ? (
                  <div className="relative">
                    <select
                      value={selectedVendor?.id || ''}
                      onChange={(e) => {
                        const vendor = vendors.find(v => v.id === e.target.value);
                        setSelectedVendor(vendor);
                        setItemsPage(1);
                      }}
                      className="text-xl font-heading font-semibold bg-transparent border-none cursor-pointer appearance-none pr-8 focus:outline-none"
                    >
                      {vendors.map(v => (
                        <option key={v.id} value={v.id}>{v.name}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-0 top-1/2 -translate-y-1/2 w-5 h-5 text-ivory-400 pointer-events-none" />
                  </div>
                ) : (
                  <CardTitle className="text-xl font-heading">
                    {selectedVendor?.name || 'No Vendors'}
                  </CardTitle>
                )}
                {selectedVendor?.base_url && (
                  <a 
                    href={selectedVendor.base_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-gold-600 hover:underline flex items-center gap-1 font-body"
                  >
                    {selectedVendor.base_url} <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
            
            {/* Stats */}
            {selectedVendor && (
              <div className="flex gap-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gold-600">{itemsTotal}</p>
                  <p className="text-xs text-ivory-500 font-body">Vendor Items</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{itemsLinked}</p>
                  <p className="text-xs text-ivory-500 font-body">Linked to Products</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-600">{itemsTotal - itemsLinked}</p>
                  <p className="text-xs text-ivory-500 font-body">Unlinked</p>
                </div>
              </div>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* Credentials Warning */}
      {needsCredentials && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-4">
          <Key className="w-8 h-8 text-amber-600" />
          <div className="flex-1">
            <p className="font-medium text-amber-800">Credentials Required</p>
            <p className="text-sm text-amber-700">
              4over requires login credentials to sync products. Add them in Settings → Vendor Logins.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => {
              // This would navigate to vendor credentials - for now just show message
              toast.info("Go to Settings → Vendor Logins to add 4over credentials");
            }}
          >
            <Settings className="w-4 h-4 mr-2" />
            Add Credentials
          </Button>
        </div>
      )}

      {/* Main Tabs */}
      {selectedVendor && (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="sync" className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              Sync from Vendor
            </TabsTrigger>
            <TabsTrigger value="items" className="flex items-center gap-2">
              <Database className="w-4 h-4" />
              Vendor Items ({itemsTotal})
            </TabsTrigger>
            <TabsTrigger value="actions" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Bulk Actions
            </TabsTrigger>
          </TabsList>

          {/* SYNC TAB */}
          <TabsContent value="sync">
            <Card className="bg-ivory-100 border border-ivory-300/50">
              <CardHeader>
                <CardTitle className="text-lg font-heading flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-gold-600" />
                  Sync from {selectedVendor.name}
                </CardTitle>
                <CardDescription>
                  {selectedVendor.slug === '4over' 
                    ? 'Scrape products from 4over wholesale portal (requires login)'
                    : 'Scrape products from the vendor website and store in vendor_items collection'
                  }
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Sync Status */}
                {isSyncing && (
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                      <div className="flex-1">
                        <p className="font-medium text-blue-800 font-body">{syncStatus.message}</p>
                        <p className="text-sm text-blue-600 font-body">
                          Found: {syncStatus.items_found} | 
                          New: {syncStatus.items_new} | 
                          Updated: {syncStatus.items_updated}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {syncStatus?.status === 'completed' && !isSyncing && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-medium text-green-800 font-body">{syncStatus.message}</p>
                        <p className="text-sm text-green-600 font-body">
                          Last sync: {formatDate(syncStatus.last_sync || syncStatus.completed_at)}
                        </p>
                        {syncStatus.price_changes?.length > 0 && (
                          <p className="text-sm text-amber-600 font-body mt-1">
                            ⚠️ {syncStatus.price_changes.length} price changes detected
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {syncStatus?.status === 'failed' && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-red-600" />
                      <div>
                        <p className="font-medium text-red-800 font-body">{syncStatus.message}</p>
                        {syncStatus.errors?.length > 0 && (
                          <p className="text-sm text-red-600 font-body mt-1">
                            {syncStatus.errors[0]}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={() => handleStartSync(false)}
                    disabled={syncing || isSyncing || needsCredentials}
                    className="btn-luxury-primary"
                    data-testid="start-sync-btn"
                  >
                    {syncing || isSyncing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4 mr-2" />
                    )}
                    {itemsTotal > 0 ? 'Sync Now' : 'Initial Sync'}
                  </Button>
                  
                  <Button
                    onClick={() => handleStartSync(true)}
                    disabled={syncing || isSyncing || needsCredentials}
                    variant="outline"
                    className="btn-luxury-outline"
                    data-testid="preview-sync-btn"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Preview Sync
                  </Button>
                </div>

                {/* Sync History */}
                {syncHistory.length > 0 && (
                  <div className="pt-4 border-t border-ivory-300/50">
                    <h3 className="font-medium text-ivory-700 mb-3 font-body flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Sync History
                    </h3>
                    <div className="space-y-2">
                      {syncHistory.map((log, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-ivory-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <CheckCircle className="w-4 h-4 text-green-600" />
                            <div>
                              <p className="text-sm font-medium text-ivory-800 font-body">
                                {formatDate(log.completed_at)}
                              </p>
                            </div>
                          </div>
                          <div className="text-right text-sm text-ivory-600 font-body">
                            {log.items_new} new, {log.items_updated} updated
                            {log.price_changes > 0 && (
                              <span className="text-amber-600 ml-2">({log.price_changes} price changes)</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ITEMS TAB */}
          <TabsContent value="items">
            <Card className="bg-ivory-100 border border-ivory-300/50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg font-heading flex items-center gap-2">
                      <Database className="w-5 h-5 text-gold-600" />
                      Vendor Items
                    </CardTitle>
                    <CardDescription>
                      Raw product data from {selectedVendor.name}
                    </CardDescription>
                  </div>
                  
                  {/* Filter Buttons */}
                  <div className="flex gap-2">
                    {['all', 'linked', 'unlinked'].map((filter) => (
                      <Button
                        key={filter}
                        variant={itemsFilter === filter ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => { setItemsFilter(filter); setItemsPage(1); }}
                        className={itemsFilter === filter ? 'bg-gold-500 text-white' : ''}
                      >
                        {filter === 'all' && 'All'}
                        {filter === 'linked' && <><Link className="w-3 h-3 mr-1" /> Linked</>}
                        {filter === 'unlinked' && <><Unlink className="w-3 h-3 mr-1" /> Unlinked</>}
                      </Button>
                    ))}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {vendorItems.length === 0 ? (
                  <div className="text-center py-8">
                    <Database className="w-12 h-12 text-ivory-400 mx-auto mb-3" />
                    <p className="text-ivory-600 font-body">No vendor items found</p>
                    <p className="text-sm text-ivory-500 font-body mt-1">Run a sync to import products from the vendor</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {vendorItems.map((item) => (
                      <div key={item.id} className="flex items-center justify-between p-4 bg-ivory-50 rounded-xl border border-ivory-200">
                        <div className="flex items-center gap-4">
                          {item.local_image_url || item.source_image_url ? (
                            <img 
                              src={item.local_image_url?.startsWith('/') ? `${API_URL}${item.local_image_url}` : item.source_image_url}
                              alt={item.source_name}
                              className="w-12 h-12 object-cover rounded-lg"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-ivory-200 rounded-lg flex items-center justify-center">
                              <Package className="w-6 h-6 text-ivory-400" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-ivory-800 font-body">{item.source_name}</p>
                            <p className="text-xs text-ivory-500 font-body">{item.source_sku}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="font-semibold text-gold-600 font-body">
                              {formatPrice(item.source_price, item.source_price_type)}
                            </p>
                            <p className="text-xs text-ivory-500 font-body">
                              {item.source_price_type === 'per_sqft' ? 'per sqft' : 'fixed'}
                            </p>
                          </div>
                          
                          {item.product_id ? (
                            <Badge className="bg-green-100 text-green-700 border-green-200">
                              <Link className="w-3 h-3 mr-1" />
                              Linked
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700 border-amber-200">
                              <Unlink className="w-3 h-3 mr-1" />
                              Unlinked
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    {/* Pagination */}
                    <div className="flex justify-center gap-2 pt-4">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={itemsPage <= 1}
                        onClick={() => setItemsPage(p => p - 1)}
                      >
                        Previous
                      </Button>
                      <span className="px-4 py-2 text-sm text-ivory-600 font-body">
                        Page {itemsPage}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={vendorItems.length < 20}
                        onClick={() => setItemsPage(p => p + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* BULK ACTIONS TAB */}
          <TabsContent value="actions">
            <div className="grid gap-4">
              {/* Update Prices Card - For 4over */}
              {selectedVendor?.slug === '4over' && (
                <Card className="bg-ivory-100 border border-gold-300/50">
                  <CardHeader>
                    <CardTitle className="text-lg font-heading flex items-center gap-2">
                      <DollarSign className="w-5 h-5 text-gold-600" />
                      Update Product Prices
                    </CardTitle>
                    <CardDescription>
                      Re-scrape prices from 4over and update all products with accurate wholesale costs
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-ivory-700 font-body">
                          Update prices for <span className="font-semibold text-gold-600">{itemsTotal}</span> products
                        </p>
                        <p className="text-sm text-ivory-500 font-body mt-1">
                          Products will be updated with 150% markup from scraped wholesale costs
                        </p>
                      </div>
                      <Button
                        onClick={handleUpdatePrices}
                        disabled={updatingPrices || priceUpdateStatus?.status === 'running' || needsCredentials || itemsTotal === 0}
                        className="btn-luxury-primary"
                        data-testid="update-prices-btn"
                      >
                        {updatingPrices || priceUpdateStatus?.status === 'running' ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <DollarSign className="w-4 h-4 mr-2" />
                        )}
                        Update Prices
                      </Button>
                    </div>
                    
                    {/* Price Update Status */}
                    {priceUpdateStatus?.status === 'running' && (
                      <div className="mt-4 bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                        <div className="flex items-center gap-3">
                          <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                          <div className="flex-1">
                            <p className="font-medium text-blue-800 font-body">{priceUpdateStatus.message}</p>
                            <p className="text-sm text-blue-600 font-body">
                              Items: {priceUpdateStatus.items_updated || 0} | Products: {priceUpdateStatus.products_updated || 0}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {priceUpdateStatus?.status === 'completed' && (
                      <div className="mt-4 bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <div>
                            <p className="font-medium text-green-800 font-body">{priceUpdateStatus.message}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
              
              {/* Create Products Card */}
              <Card className="bg-ivory-100 border border-ivory-300/50">
                <CardHeader>
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <Plus className="w-5 h-5 text-green-600" />
                    Create Products from Vendor Items
                  </CardTitle>
                  <CardDescription>
                    Create products in your catalog for all unlinked vendor items
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-ivory-700 font-body">
                        <span className="font-semibold text-amber-600">{itemsTotal - itemsLinked}</span> unlinked items ready to become products
                      </p>
                      <p className="text-sm text-ivory-500 font-body mt-1">
                        Products will be created with 150% markup (vendor price becomes cost_price)
                      </p>
                    </div>
                    <Button
                      onClick={handleCreateProducts}
                      disabled={creatingProducts || (itemsTotal - itemsLinked) === 0}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid="create-products-btn"
                    >
                      {creatingProducts ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Plus className="w-4 h-4 mr-2" />
                      )}
                      Create All Products
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Clear Items Card */}
              <Card className="bg-ivory-100 border border-red-200/50">
                <CardHeader>
                  <CardTitle className="text-lg font-heading flex items-center gap-2 text-red-600">
                    <AlertCircle className="w-5 h-5" />
                    Clear Vendor Items
                  </CardTitle>
                  <CardDescription>
                    Delete all vendor items (for a fresh re-sync)
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-ivory-700 font-body">
                        This will delete <span className="font-semibold text-red-600">{itemsTotal}</span> vendor items
                      </p>
                      <p className="text-sm text-ivory-500 font-body mt-1">
                        Products will be unlinked but not deleted
                      </p>
                    </div>
                    <Button
                      onClick={handleClearItems}
                      disabled={loading || itemsTotal === 0}
                      variant="destructive"
                      data-testid="clear-items-btn"
                    >
                      Clear Items
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      )}

      {/* No vendors message */}
      {vendors.length === 0 && !loadingVendors && (
        <Card className="bg-ivory-50">
          <CardContent className="py-12 text-center">
            <Store className="w-16 h-16 mx-auto text-ivory-300 mb-4" />
            <h3 className="text-lg font-medium text-ivory-700 mb-2">No Syncable Vendors</h3>
            <p className="text-ivory-500">
              No vendors are configured for automatic product syncing.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default VendorSyncHub;
